import { useMemo } from 'react';

import { ClusterDisplayStatus } from '@cast/types';

import {
  useClusterOverviewQuery,
  type CostReportClusterSummaryNormalized,
} from '@cast/shared-cost-data-access';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import {
  getDiscoveredMetricsFromCluster,
  mapClusterOverviewToSummaryNormalized,
} from '@cast/features-cluster-shared-utils';

import { useClusterDashboardOverviewQueryArgs } from './useClusterDashboardOverviewQueryArgs';

export type UseClusterDashboardGaugeData = {
  summary?: CostReportClusterSummaryNormalized;
  isLoading: boolean;
  hasError: boolean;
  refetch: () => void;
};

export const useClusterDashboardGaugeData = (
  disabled = false
): UseClusterDashboardGaugeData => {
  const { cluster } = useCluster();
  const { startTime, endTime, stepSeconds } =
    useClusterDashboardOverviewQueryArgs();
  const isDiscovered =
    cluster.displayStatus === ClusterDisplayStatus.DISCOVERED;

  const {
    data: overview,
    isLoading,
    error,
    refetch,
  } = useClusterOverviewQuery({
    clusterId: cluster.id,
    startTime,
    endTime,
    stepSeconds,
    enabled: !disabled && !isDiscovered,
  });

  const summary = useMemo(() => {
    const fromOverview = mapClusterOverviewToSummaryNormalized(overview);
    if (fromOverview) {
      return fromOverview;
    }
    if (isDiscovered) {
      return getDiscoveredMetricsFromCluster(cluster);
    }
    return undefined;
  }, [cluster, isDiscovered, overview]);

  return {
    summary,
    isLoading,
    hasError: !!error,
    refetch,
  };
};
